// Daily Briefing — Phase 5. Today's AI-generated P1/P2/P3 priorities plus
// a rolling 14-day history of past headlines so supers can scan what
// shifted day-to-day.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { Briefing } from '@greeniq/shared';
import { ApiError } from '../api/client.js';
import { getBriefingHistory } from '../api/briefings.js';
import { PageHeader } from '../components/ui/page-header';
import { BriefingCard } from '../components/BriefingCard.js';

export function BriefingPage() {
  const { courseSlug } = useParams();
  const [history, setHistory] = useState<Briefing[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!courseSlug) return;
    void getBriefingHistory(courseSlug, 14)
      .then((r) => setHistory(r.briefings))
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, [courseSlug]);

  if (!courseSlug) return null;
  return (
    <div className="max-w-6xl p-6 md:p-10">
      <PageHeader
        eyebrow="Phase 5"
        title="Daily Briefing"
        description="Today's AI-generated P1 / P2 / P3 priorities, plus the last two weeks of headlines for context."
      />
      <div className="mt-8">
        <BriefingCard courseSlug={courseSlug} />
      </div>
      <div className="mt-10">
        <h3 className="font-display text-xs font-semibold uppercase tracking-[0.15em] text-fg-muted">
          Recent briefings
        </h3>
        {error && <p className="mt-2 text-sm text-[color:var(--color-error)]">{error}</p>}
        {!error && history === null && (
          <p className="mt-2 text-sm text-fg-muted">Loading history…</p>
        )}
        {history && history.length === 0 && (
          <p className="mt-2 text-sm text-fg-muted">No briefings in the last 14 days.</p>
        )}
        {history && history.length > 0 && (
          <ul className="mt-3 space-y-2">
            {history.map((b) => (
              <li
                key={b.id}
                className="rounded-md border border-border bg-bg-card px-4 py-3 flex flex-wrap items-baseline gap-x-4 gap-y-1"
              >
                <span className="font-mono text-xs text-fg-muted tabular-nums">{b.forDate}</span>
                <span className="text-sm font-semibold text-fg flex-1 min-w-0">
                  {b.headline ?? <em className="text-fg-muted">No headline</em>}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
